import React from 'react';
import { Alert, Button, Spinner, formatServiceArea } from '@/components/ui';
import { KPIHomeWidget } from './KPIHomeWidget';
import type { RedirectConfig } from './ParentWidget';
import type { CriticalDefectsSummary } from '../../services/vehicleDefectsService';

/** The Reports id of the Open Defects report (Equipment category). */
export const OPEN_DEFECTS_REPORT_ID = 'equipment-open-defects';

/** Where a tile drills through to: the Open Defects report, filtered to Critical and the area. */
export function criticalDefectsReportLink(serviceAreaId?: number | null): RedirectConfig {
  return {
    path: '/reports',
    queryParams: {
      report: OPEN_DEFECTS_REPORT_ID,
      severity: 'Critical',
      status: 'Open',
      ...(serviceAreaId ? { serviceAreaId } : {}),
    },
  };
}

export function openCriticalDefectsLabel(count: number): string {
  return count === 1 ? 'Open critical defect' : 'Open critical defects';
}

export interface CriticalDefectsWidgetProps {
  summary: CriticalDefectsSummary | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  requiredPermission: string;
}

/**
 * EQ-30: open Critical defects as count tiles, one for the
 * total and one per service area. Counts only - no lists on Home (DASH-01). Each tile opens the
 * Open Defects report filtered to Critical and that service area.
 */
const CriticalDefectsWidget: React.FC<CriticalDefectsWidgetProps> = ({
  summary,
  loading,
  error,
  onRetry,
  requiredPermission,
}) => {
  if (loading && !summary) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-py-2" role="status">
        <Spinner animation="border" size="sm" />
        <span className="tw-text-sm tw-text-content-muted">Loading critical defects…</span>
      </div>
    );
  }

  // STATE-02: an error is said inline with a way to try again, never shown as zero.
  if (error) {
    return (
      <Alert variant="danger" className="tw-mb-0 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span>Critical defects could not be loaded. {error.message}</span>
        <Button size="sm" variant="outline-danger" onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  const total = summary?.total ?? 0;
  const areas = summary?.byServiceArea ?? [];

  return (
    <div className="kpi-widgets-grid" data-testid="critical-defects-tiles">
      <div className="tw-h-full">
        <KPIHomeWidget
          id="critical-defects-total"
          title="Critical Defects – All Service Areas"
          size="small"
          requiredPermission={requiredPermission}
          data={{
            value: total,
            description: total === 0 ? 'No open critical defects' : openCriticalDefectsLabel(total),
          }}
          redirect={criticalDefectsReportLink()}
          metadata={{ loading: false, error: null, isEmpty: false }}
          onRefresh={onRetry}
        />
      </div>
      {areas.map((area) => (
        <div key={area.serviceAreaId ?? 'none'} className="tw-h-full">
          <KPIHomeWidget
            id={`critical-defects-sa-${area.serviceAreaId ?? 'none'}`}
            title={formatServiceArea({ id: area.serviceAreaId, name: area.name, code: area.code }) ?? area.name}
            size="small"
            requiredPermission={requiredPermission}
            data={{ value: area.count, description: openCriticalDefectsLabel(area.count) }}
            // Units with no service area can't be filtered to in the report; their tile only counts.
            redirect={area.serviceAreaId ? criticalDefectsReportLink(area.serviceAreaId) : undefined}
            metadata={{ loading: false, error: null, isEmpty: false }}
            onRefresh={onRetry}
          />
        </div>
      ))}
    </div>
  );
};

export default CriticalDefectsWidget;
